import { useState, useCallback } from 'react';
import {
  fetchUsers as fetchUsersApi,
  createUser,
  updateUser,
  deleteUser as deleteUserApi,
  toggleUserStatus,
  sendInvitation,
  getInvitations,
  listRoles,
  createRoleApi,
  updateRole,
  deleteRole,
} from '../services/adminService';

export function useAdminData() {
  // User state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [userSearch, setUserSearch] = useState('');
  const [userFilters, setUserFilters] = useState({});

  // User modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Role state
  const [roles, setRoles] = useState([]);
  const [originalRoles, setOriginalRoles] = useState([]);
  const [rolesCurrentPage, setRolesCurrentPage] = useState(1);
  const [rolesTotalPages, setRolesTotalPages] = useState(1);
  const [rolesTotalCount, setRolesTotalCount] = useState(0);
  const [rolesPageSize, setRolesPageSize] = useState(10);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);

  // Invitation state
  const [invitations, setInvitations] = useState([]);

  // --- Fetch functions ---

  const fetchUsers = useCallback(async ({ pageNumber = currentPage, pageSize: size = pageSize, search = userSearch, filters = userFilters } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        pageNumber,
        pageSize: size,
        search: search || undefined,
      };

      if (filters.role) params.role = filters.role;
      if (filters.name) params.name = filters.name;
      if (filters.email) params.email = filters.email;
      if (filters.isActive) params.isActive = filters.isActive;
      if (filters.createdAt?.start) params.createdAtStart = filters.createdAt.start;
      if (filters.createdAt?.end) params.createdAtEnd = filters.createdAt.end;

      const data = await fetchUsersApi(params);
      setUsers(data.users);
      setTotalPages(data.pages);
      setTotalCount(data.totalCount || data.total || 0);
      setCurrentPage(data.page || pageNumber);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, userSearch, userFilters]);

  const fetchInvites = useCallback(async () => {
    try {
      const data = await getInvitations();
      setInvitations(data.invitations || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async (filters = {}) => {
    try {
      const data = await listRoles();
      const rolesArray = Array.isArray(data.roles) ? data.roles : [];

      let filteredRoles = rolesArray;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredRoles = filteredRoles.filter(role =>
          role.name.toLowerCase().includes(searchLower) ||
          (role.description && role.description.toLowerCase().includes(searchLower))
        );
      }
      if (filters.name) {
        const nameLower = filters.name.toLowerCase();
        filteredRoles = filteredRoles.filter(role =>
          role.name.toLowerCase().includes(nameLower)
        );
      }

      setRoles(filteredRoles);
      setOriginalRoles(rolesArray);
      setRolesTotalCount(filteredRoles.length);
      setRolesTotalPages(Math.ceil(filteredRoles.length / rolesPageSize));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  }, [rolesPageSize]);

  // --- User handlers ---

  const openModal = (user, mode) => {
    setSelectedUser(user);
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setModalMode('view');
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleInviteUser = () => setIsInviteModalOpen(true);
  const closeInviteModal = () => setIsInviteModalOpen(false);

  const handleSaveUser = async (userData) => {
    try {
      if (userData.isActive !== undefined) {
        userData.isActive = userData.isActive === 'true';
      }
      if (modalMode === 'create') {
        await createUser(userData);
      } else if (modalMode === 'edit' && selectedUser) {
        await updateUser(selectedUser._id, userData);
      }
      fetchUsers();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save user');
    }
  };

  const handleSendInvitation = async (inviteData) => {
    try {
      await sendInvitation(inviteData);
      fetchUsers();
      closeInviteModal();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send invitation');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await toggleUserStatus(user._id, !user.isActive);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUserApi(userId);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete user');
    }
  };

  // --- User pagination/sort/filter ---

  const handleSearchChange = (term) => {
    setUserSearch(term);
    setCurrentPage(1);
    fetchUsers({ pageNumber: 1, search: term });
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchUsers({ pageNumber: newPage });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchUsers({ pageNumber: 1, pageSize: newPageSize });
  };

  const handleUsersSort = (key, direction) => {
    const sorted = [...users].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setUsers(sorted);
  };

  const handleUsersFilter = (filters) => {
    setUserFilters(filters);
    setCurrentPage(1);
    fetchUsers({ pageNumber: 1, filters });
  };

  // --- Role handlers ---

  const handleCreateRole = async ({ name, description, permissions }) => {
    try {
      await createRoleApi({ name, description, permissions });
      setIsRoleModalOpen(false);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create role');
    }
  };

  const handleUpdateRole = async ({ id, name, description, permissions }) => {
    try {
      await updateRole(id, { name, description, permissions });
      setEditRole(null);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await deleteRole(roleId);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete role');
    }
  };

  // --- Role pagination/sort/filter ---

  const handleRolesPageChange = (newPage) => {
    if (newPage > 0 && newPage <= rolesTotalPages) setRolesCurrentPage(newPage);
  };

  const handleRolesPageSizeChange = (newPageSize) => {
    setRolesPageSize(newPageSize);
    setRolesCurrentPage(1);
  };

  const handleRolesSort = (key, direction) => {
    const sorted = [...roles].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setRoles(sorted);
  };

  const handleRolesFilter = (filters) => {
    let filtered = [...originalRoles];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter(role =>
        role.name.toLowerCase().includes(term) ||
        role.description?.toLowerCase().includes(term) ||
        role.permissions?.some(perm => perm.toLowerCase().includes(term))
      );
    }

    Object.keys(filters).forEach(key => {
      if (key !== 'search' && filters[key]) {
        filtered = filtered.filter(role => {
          const value = role[key];
          if (key === 'createdAt' && filters[key]) {
            return new Date(value).toDateString() === new Date(filters[key]).toDateString();
          }
          return value && value.toString().toLowerCase().includes(filters[key].toLowerCase());
        });
      }
    });

    setRoles(filtered);
    setRolesTotalCount(filtered.length);
    setRolesTotalPages(Math.ceil(filtered.length / rolesPageSize));
    setRolesCurrentPage(1);
  };

  return {
    // User state
    users, loading, error, setError,
    currentPage, totalPages, totalCount, pageSize,
    selectedUser, isModalOpen, modalMode, isInviteModalOpen,
    userSearch, userFilters,

    // User actions
    fetchUsers, fetchInvites,
    openModal, closeModal, handleCreateUser,
    handleInviteUser, closeInviteModal,
    handleSaveUser, handleSendInvitation,
    handleToggleUserStatus, handleDeleteUser,
    handleSearchChange, handlePageChange, handlePageSizeChange,
    handleUsersSort, handleUsersFilter,

    // Role state
    roles, rolesCurrentPage, rolesTotalPages, rolesTotalCount, rolesPageSize,
    isRoleModalOpen, setIsRoleModalOpen, editRole, setEditRole,

    // Role actions
    fetchRoles,
    handleCreateRole, handleUpdateRole, handleDeleteRole,
    handleRolesPageChange, handleRolesPageSizeChange,
    handleRolesSort, handleRolesFilter,

    // Invitations
    invitations,
  };
}
